import { graphql } from '~/graphql/generated/gql';

/**
 * What every surface needs to render an asset's curriculum tags: the node, and the framework it
 * belongs to (a bare node code means nothing without it). Every asset document that shows the tag
 * cell spreads this, so adding a field the cell needs is one edit rather than seven.
 *
 * Both `key`s are selected because a tag is shown as `frameworkKey.nodeKey` — the pair is unique
 * (`curriculum_nodes` is unique on `framework_id, key`) and survives a reworded name. The tree
 * query already carries them for live nodes; here they are what a node withdrawn from a later
 * revision of its file still has to name itself with, having left the tree.
 */
export const curriculumTagFieldsFragment = graphql(`
  fragment CurriculumTagFields on CurriculumTag {
    id
    node {
      id
      key
      code
      name
      description
    }
    framework {
      id
      key
      name
    }
    frameworkToneIndex
    ancestors
  }
`);

/**
 * The same selection as {@link curriculumTagFieldsFragment}, read off the admin projection. The
 * content pages reach their assets through the admin roots, so their tags arrive as
 * `AdminCurriculumTag` rows and need a fragment declared on that type.
 */
export const adminCurriculumTagFieldsFragment = graphql(`
  fragment AdminCurriculumTagFields on AdminCurriculumTag {
    id
    node {
      id
      key
      code
      name
      description
    }
    framework {
      id
      key
      name
    }
    frameworkToneIndex
    ancestors
  }
`);

/**
 * What a lesson teaches, as the overview renders it. The server settles every derivation rule and
 * drops the sections the lesson has nothing for, so this is read, never recomputed: a second
 * derivation on the client would drift from the browse filters reading the same roll-up.
 *
 * Sections rather than named rows, so a lesson tagged against a curriculum with different rows
 * arrives through the same selection instead of needing fields added to it.
 *
 * The whole field is null when the lesson carries nothing, which is what keeps the block absent
 * rather than a column of empty labels.
 */
export const lessonCurriculumRollupFragment = graphql(`
  fragment LessonCurriculumRollupFields on LessonCurriculumRollup {
    pages {
      framework {
        key
        name
      }
      toneIndex
      tags {
        id
        key
        code
        name
        description
        ancestors
        frameworkId
        frameworkKey
        frameworkName
        frameworkToneIndex
      }
      sections {
        key
        kind
        entries {
          text
          source
        }
      }
    }
  }
`);

export const curriculumFrameworks = graphql(`
  query curriculumFrameworks {
    curriculumFrameworks {
      id
      key
      name
      levels
      nodesCount
    }
  }
`);

export const curriculumFrameworkCoverage = graphql(`
  query curriculumFrameworkCoverage($key: String!) {
    curriculumFrameworkCoverage(key: $key) {
      id
      key
      name
      levels
      states {
        id
        name
      }
      nodes {
        id
        code
        name
        parentId
        sortOrder
        tagsCount
      }
    }
  }
`);

export const curriculumNodeCoverage = graphql(`
  query curriculumNodeCoverage($id: ID!) {
    curriculumNodeCoverage(id: $id) {
      id
      tagsCount
      taggedAssets {
        id
        name
        type
      }
    }
  }
`);

export const setLearningAssetCurriculumTags = graphql(`
  mutation setLearningAssetCurriculumTags($input: SetLearningAssetCurriculumTagsInput!) {
    setLearningAssetCurriculumTags(input: $input) {
      id
      curriculumTags {
        ...AdminCurriculumTagFields
      }
    }
  }
`);

export const setCurriculumFrameworkStates = graphql(`
  mutation setCurriculumFrameworkStates($input: SetCurriculumFrameworkStatesInput!) {
    setCurriculumFrameworkStates(input: $input) {
      id
      states {
        id
        name
      }
    }
  }
`);

/**
 * The region's whole vocabulary with its trees. Every curriculum control reads this one document
 * without variables, so a page holding a picker and its chips sends one request for it.
 */
export const curriculumFrameworksWithNodes = graphql(`
  query curriculumFrameworksWithNodes {
    curriculumFrameworks {
      id
      key
      name
      levels
      toneIndex
      nodes {
        id
        key
        code
        name
        parentId
        sortOrder
      }
    }
  }
`);

/**
 * Which frameworks are available in a school's state, as ids only. A teacher picker narrows the
 * shared tree above with these rather than fetching a second, filtered copy of the tree.
 */
export const curriculumFrameworkIdsForSchool = graphql(`
  query curriculumFrameworkIdsForSchool($schoolId: ID!) {
    curriculumFrameworksForSchool(schoolId: $schoolId) {
      id
    }
  }
`);
